import { toBybitInterval } from "../../candle-intervals";
import { normalizeCandleSymbol } from "../../candle-symbols";
import {
  CandleInterval,
  ExchangeCandleWsAdapter,
} from "../../candle.types";
import { RECONNECT_DELAYS_MS } from "./ws-candle.utils";

interface BybitKlineItem {
  start: number;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
}

interface BybitWsMessage {
  topic?: string;
  ts?: number;
  data?: BybitKlineItem[];
}

const PING_INTERVAL_MS = 20_000;

function wsInterval(interval: CandleInterval): string {
  return toBybitInterval(interval);
}

export const bybitCandlesWsAdapter: ExchangeCandleWsAdapter = {
  subscribe(symbol, interval, onUpdate, onError) {
    const normalized = normalizeCandleSymbol("bybit", symbol);
    const topic = `kline.${wsInterval(interval)}.${normalized}`;
    let ws: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let pingTimer: ReturnType<typeof setInterval> | null = null;

    const clearPing = () => {
      if (pingTimer) clearInterval(pingTimer);
      pingTimer = null;
    };

    const connect = () => {
      if (stopped) return;
      ws = new WebSocket("wss://stream.bybit.com/v5/public/linear");

      ws.onopen = () => {
        ws?.send(JSON.stringify({ op: "subscribe", args: [topic] }));
        pingTimer = setInterval(() => {
          ws?.send(JSON.stringify({ op: "ping" }));
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const message = JSON.parse(event.data) as BybitWsMessage;
          if (!message.topic?.startsWith("kline.")) return;
          const candles = message.data ?? [];
          candles.forEach((candle) => {
            onUpdate({
              symbol: normalized,
              exchange: "bybit",
              interval,
              candle: {
                startTime: candle.start,
                openPrice: candle.open,
                highPrice: candle.high,
                lowPrice: candle.low,
                closePrice: candle.close,
                volume: candle.volume,
              },
              timestamp: message.ts ?? Date.now(),
            });
          });
          attempt = 0;
        } catch {
          // Ignore malformed payloads
        }
      };

      ws.onerror = () => {
        // onclose handles retries
      };

      ws.onclose = () => {
        clearPing();
        if (stopped) return;
        const delay =
          RECONNECT_DELAYS_MS[
            Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)
          ];
        attempt += 1;
        if (attempt > RECONNECT_DELAYS_MS.length) {
          onError?.(new Error(`bybit: live candle stream disconnected`));
          return;
        }
        setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      stopped = true;
      clearPing();
      ws?.close();
      ws = null;
    };
  },
};

